/**
 * Merging daily metrics from several sources.
 *
 * THE RULE: a value the user entered by hand is never overwritten by a sync.
 *
 * Weight is the case that makes this matter. The scale pushes to Apple Health,
 * Apple Health pushes here, and Oura reports its own activity figures -- but
 * if Ben typed a weight this morning, that is the number he meant, and a later
 * Health sync reporting yesterday's scale reading must not replace it.
 *
 * Precedence is by source first, recency second. Within the same source, newer
 * wins; across sources, a higher-precedence source wins even if older.
 */
import { nowIso } from '../lib/time';
import type { DailyMetrics, Day, Reading, Source } from './schema';

/** Higher number wins. */
const PRECEDENCE: Record<Source, number> = {
  manual: 100,
  scale: 60,
  apple_health: 50,
  oura: 40,
  import: 30,
  derived: 10,
};

/** Decide which of two readings for the same field survives. */
export function preferReading(a: Reading | undefined, b: Reading | undefined): Reading | undefined {
  if (!a) return b;
  if (!b) return a;
  const pa = PRECEDENCE[a.source] ?? 0;
  const pb = PRECEDENCE[b.source] ?? 0;
  if (pa !== pb) return pa > pb ? a : b;
  return a.at >= b.at ? a : b;
}

/**
 * Merge incoming field readings into an existing day.
 * Returns a new record; the input is not mutated.
 */
export function mergeDay(
  existing: DailyMetrics | undefined, day: Day, incoming: Record<string, Reading>,
): DailyMetrics {
  const base: DailyMetrics = existing
    ? { ...existing }
    : { day, updatedAt: nowIso() };

  let changed = false;
  for (const [field, reading] of Object.entries(incoming)) {
    const current = (base as unknown as Record<string, Reading | undefined>)[field];
    const winner = preferReading(current, reading);
    if (winner !== current) {
      (base as unknown as Record<string, Reading | undefined>)[field] = winner;
      changed = true;
    }
  }
  if (changed) base.updatedAt = nowIso();
  return base;
}

/** Convenience for recording a value the user typed. */
export function manualReading(value: number, at: string = nowIso()): Reading {
  return { value, source: 'manual', at };
}

/** Read a field's number, ignoring provenance. */
export function valueOf(metrics: DailyMetrics | undefined, field: keyof DailyMetrics): number | undefined {
  const reading = metrics?.[field] as Reading | undefined;
  return reading && typeof reading === 'object' && 'value' in reading ? reading.value : undefined;
}

/**
 * The most recent value of a field across a set of days, newest first.
 * Used for "latest weight" where today may have no reading.
 */
export function latestValue(
  days: DailyMetrics[], field: keyof DailyMetrics,
): { value: number; day: Day; source: Source } | null {
  const sorted = [...days].sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0));
  for (const d of sorted) {
    const reading = d[field] as Reading | undefined;
    if (reading && typeof reading === 'object' && 'value' in reading) {
      return { value: reading.value, day: d.day, source: reading.source };
    }
  }
  return null;
}

/** Simple moving average over a series, ignoring gaps. */
export function movingAverage(values: (number | undefined)[], window: number): (number | null)[] {
  const out: (number | null)[] = [];
  for (let i = 0; i < values.length; i++) {
    const slice = values.slice(Math.max(0, i - window + 1), i + 1).filter((v): v is number => typeof v === 'number');
    out.push(slice.length ? slice.reduce((a, b) => a + b, 0) / slice.length : null);
  }
  return out;
}
